import { Link, useParams } from 'react-router-dom';
import {
  filterPlayersByDefault,
  isRelevantPosition,
  transformToDisplayData
} from '@bo-utils/simulations';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { PATHS } from '@bo-constants/routes';
import { SimulationDetailsPageHeader } from '@bo-components/simulations/SimulationDetailsPageHeader';
import { SimulationSteps } from '@bo-components/simulations/SimulationSteps';
import { getSimulationResponse } from '@bo-test/mock/data/simulations';
import { useGame } from '@bo-hooks/useGame';
import { useGetMarketsQuery } from '@bo-services/simulationsApi';
import { useGetTeamPlayersQuery } from '@bo-services/playersApi';
import { useLeagues } from '@bo-hooks/useLeagues';
import { useMemo } from 'react';
import { useSimulation } from '@bo-hooks/simulations/useSimulation';
import { useSimulations } from '@bo-hooks/simulations/useSimulations';

export const SimulationDetails = () => {
  const {
    gameId = '',
    leagueId = '',
    simulationId = ''
  } = useParams<{
    gameId: string;
    leagueId: string;
    simulationId: string;
  }>();

  const { game, isLeagueGameLoading, isLeagueGameError } = useGame({
    gameId,
    leagueId
  });

  const { isLeagueError, isLeagueLoading, leagueInfo } = useLeagues({
    initialId: leagueId
  });

  const { simulation, isSimulationLoading, isSimulationError } = useSimulation({
    gameId,
    gameStartTime: game?.scheduled_at,
    leagueId,
    simulationId
  });

  const { simulations, isSimulationsLoading } = useSimulations({
    gameId,
    leagueId
  });

  const defaultSimulationId =
    simulations &&
    (simulations?.find(
      (sim) => sim.attributes.simulation_run_type === 'PUBLISHED'
    )?.id ||
      simulations?.find(
        (sim) => sim.attributes.simulation_run_type === 'CANDIDATE'
      )?.id ||
      simulations?.[0]?.id);

  const { simulation: defaultSimData, isSimulationLoading: defaultSimLoading } =
    useSimulation({
      gameId,
      gameStartTime: game?.scheduled_at,
      leagueId,
      simulationId: defaultSimulationId
    });

  // TODO: handle loading states
  const {
    data: gamePlayers,
    isLoading: isPlayersLoading,
    isFetching: isPlayersFetching,
    isError: isPlayersFetchError
  } = useGetTeamPlayersQuery(
    {
      leagueId,
      gameDate: game?.scheduled_at || '',
      teams: `${game?.home_team_id || ''},${game?.away_team_id || ''}`
    },
    { skip: !game }
  );

  const relevantPlayers = useMemo(() => {
    return gamePlayers?.filter((player) => isRelevantPosition(player));
  }, [gamePlayers]);

  const noDefaultSim =
    !isSimulationsLoading &&
    relevantPlayers &&
    relevantPlayers.length > 0 &&
    !defaultSimLoading &&
    defaultSimData === undefined;

  const currentSimulation = transformToDisplayData(simulation);

  const defaultSimulation = noDefaultSim
    ? getSimulationResponse({ game, gamePlayers: relevantPlayers })
    : transformToDisplayData(defaultSimData);

  const relevantPlayersDeterminedByDefault = useMemo(() => {
    return noDefaultSim
      ? relevantPlayers
      : filterPlayersByDefault({
          defaultSimulation,
          players: relevantPlayers || []
        });
  }, [defaultSimulation, noDefaultSim, relevantPlayers]);

  const { data } = useGetMarketsQuery({ league_id: leagueId });

  const { markets: allMarkets } = data || {};

  const pageLoading =
    !allMarkets ||
    !game ||
    !gamePlayers ||
    !relevantPlayers ||
    relevantPlayersDeterminedByDefault.length === 0 ||
    !leagueInfo ||
    isLeagueLoading ||
    isPlayersLoading ||
    isPlayersFetching ||
    isLeagueGameLoading ||
    !defaultSimulation ||
    isSimulationLoading;

  const isNewSimulation = simulationId === 'NEW';

  const pageError =
    isLeagueError ||
    isLeagueGameError ||
    isPlayersFetchError ||
    (!isNewSimulation && isSimulationError);

  return (
    <div className="h-full w-auto flex-col">
      <SimulationDetailsPageHeader
        awayTeamName={game?.away_team_name}
        homeTeamName={game?.home_team_name}
        isNewSimulation={isNewSimulation}
        scheduledAt={game?.scheduled_at}
        simulationName={currentSimulation?.data?.attributes.simulation_run_name}
        simulationType={currentSimulation?.data?.attributes.simulation_run_type}
      />

      {pageError && (
        <div>
          <NoResults label="There has been an error. Please try again." />
          <Link
            className="bg-stankonia-100 text-soClean-100 disabled:bg-stankonia-300 disabled:text-soClean-180 mx-8 flex flex-row items-center justify-center rounded-md px-3 py-2 text-base font-medium disabled:cursor-not-allowed"
            to={`${PATHS.SIMULATIONS}/${leagueId}/${gameId}`}
          >
            Back to Simulation List
          </Link>
        </div>
      )}

      {!pageError && pageLoading && (
        <Loading loadingMessage="Loading simulation parameters" />
      )}

      {!pageError && !pageLoading && (
        <div className="px-9">
          <SimulationSteps
            allMarkets={allMarkets}
            defaultSimulation={defaultSimulation}
            game={game}
            gamePlayers={gamePlayers}
            isNewSimulation={isNewSimulation}
            leagueInfo={leagueInfo}
            relevantPlayers={relevantPlayersDeterminedByDefault}
            simulationId={simulationId}
            simulation={currentSimulation}
          />
        </div>
      )}
    </div>
  );
};
